import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import './device-details.styles.scss';

import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import useMediaQuery from '@mui/material/useMediaQuery';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckIcon from '@mui/icons-material/Check';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

import balancingSchema from './balancingSchema.json';
import balancingUiSchema from './balancingUiSchema.json';
import { DeviceConfigWizardDialog } from '../device-config-wizard-component';
import { ConfigurationPart } from './device-details-configuration.part';
import { ErrorBoundary } from '@lifepowr/components/src/components-shared/error-boundary-component/error-boundary.component';
import { LinkPart } from './device-details-link.part';
import { BalancingPart, type BalancingPartProps } from './device-details-balancing.part';

import getClientInfo from '@lifepowr/components/src/clients';
import { generatedAccountStatus } from '@lifepowr/components/src/utils/account-status';

type DeviceDetailsProps = {
  admin: boolean;
  userMode: boolean;
  shadow: any;
  deviceId: string;
  attributes: any;
  connected: boolean;
  advanced: boolean;
  thing: any;
};

/**
 * @returns A component with a form and a set of inputs for this device.
 */
function DeviceDetailsContent(props: DeviceDetailsProps) {
  const { admin: isUserAnAdmin, userMode: renderUiInUserMode, shadow, deviceId, attributes, connected, advanced: hasUserEnabledAdvancedUi, thing } = props;

  const location = useLocation();
  const history = useHistory();

  const {
    desired: { settings: desiredSettings, configuration: desiredConfiguration, balancing, cfu, additionalInformation = {} } = {},
    reported: { settings: reportedSettings, configuration: reportedConfiguration } = {},
  } = shadow || {};

  const desiredConfigurationTimestamp = shadow?.metadata.desired.configuration?.address?.timestamp || 0;
  const reportedConfigurationTimestamp = shadow?.metadata.reported.configuration?.address?.timestamp || 0;
  const desiredSettingsTimestamp = shadow?.metadata.settings?.desired.VAT.timestamp || 0;
  const reportedSettingsTimestamp = shadow?.metadata.settings?.reported.VAT.timestamp || 0;

  const { account, email } = attributes || {};
  const accountStatus = generatedAccountStatus(account, email);
  const thingName = deviceId;

  const [isOnboardingWizardActive, setOnboardingWizardActive] = useState(false);
  const [expanded, setExpanded] = useState('config');
  const [balancingSchemaEdited] = useState(balancingSchema);

  const { ean, optOutFcr, fcr = {} } = balancing || {};

  const { bsp } = fcr || {};
  const formData = {
    ean,
  };

  let BalanceIcon = WarningAmberIcon;
  let balanceColor = 'red';
  let tooltip = 'Balancing information is required';

  if (ean && !bsp) {
    BalanceIcon = TimelapseIcon;
    balanceColor = 'gold';
    tooltip = 'Evaluation';
  } else if (ean && bsp) {
    BalanceIcon = CheckIcon;
    balanceColor = 'green';
    tooltip = 'Ready to participate';
  }

  const screenSizeIsSmall = useMediaQuery('(min-width:900px)');
  const screenSizeIsMedium = useMediaQuery('(min-width:1200px)');

  let width = 'xs';
  if (screenSizeIsSmall) {
    width = 'sm';
  } else if (screenSizeIsMedium) {
    width = 'md';
  }

  const balancingProps: BalancingPartProps = {
    schema: balancingSchemaEdited,
    uiSchema: balancingUiSchema,
    formData,
    thingName,
    optOutFcr,
    balancing,
    isUserAnAdmin,
  };

  const linkProps = {
    accountStatus,
    email,
    width,
    renderUiInUserMode,
    thingName,
  };

  const configurationComponent = useMemo(() => {
    const configProps = {
      renderUiInUserMode,
      hasUserEnabledAdvancedUi,
      thingName,
      desiredSettings,
      reportedSettings,
      desiredConfiguration,
      reportedConfiguration,
      settingsInTransit: desiredSettingsTimestamp > reportedSettingsTimestamp,
      configurationInTransit: desiredConfigurationTimestamp > reportedConfigurationTimestamp,
      cfu,
      additionalInformation,
    };

    return <ConfigurationPart {...configProps} />;
  }, [desiredConfigurationTimestamp, reportedConfigurationTimestamp, desiredSettingsTimestamp, reportedSettingsTimestamp, connected, thingName]);

  const clientInfo = getClientInfo();

  const showEanFederated = clientInfo ? clientInfo?.ean : true;
  const showEanShow = true;

  const activateOnQueryParam = (queryParamName = '', callback = () => {}) => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has(queryParamName)) {
      if (callback) {
        callback();
      }
      queryParams.delete(queryParamName);
      history.replace({
        search: queryParams.toString(),
      });
    }
  };

  useEffect(() => {
    activateOnQueryParam('wizard', () => setOnboardingWizardActive(true));
  }, []);

  const configurationWizard = useMemo(() => {
    const configurationWizardProps = {
      renderUiInUserMode,
      advanced: hasUserEnabledAdvancedUi,
      width,
      thingName,
      thing,
      attributes: thing?.attributes,

      settings: desiredSettings,
      configuration: desiredConfiguration,
      diffObj: { settings: {}, configuration: {} },
    };

    return (
      <DeviceConfigWizardDialog
        isOpen={isOnboardingWizardActive}
        onClose={() => setOnboardingWizardActive(false)}
        {...configurationWizardProps}
      />
    );
  }, [desiredConfigurationTimestamp, desiredSettingsTimestamp, thingName, isOnboardingWizardActive]);

  return (
    <div className="device-details">
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          <LinkPart {...linkProps} />
        </Box>

        <Box alignSelf="end">{configurationWizard}</Box>
      </Box>

      <Divider />

      {showEanFederated && showEanShow && (
        <Accordion
          expanded={expanded === 'balance'}
          onChange={() => setExpanded('balance')}
        >
          <AccordionSummary>
            <Box
              display="flex"
              flexDirection="row"
              gap="10px"
              alignItems="center"
            >
              <Tooltip title={tooltip}>
                <BalanceIcon sx={{ color: balanceColor }} />
              </Tooltip>

              <Typography variant="h5">Balancing</Typography>
            </Box>
          </AccordionSummary>

          <AccordionDetails>
            <BalancingPart {...balancingProps} />
          </AccordionDetails>
        </Accordion>
      )}

      <Accordion
        expanded={expanded === 'config'}
        onChange={() => setExpanded('config')}
      >
        <AccordionSummary>
          <Typography variant="h5">Configuration</Typography>
        </AccordionSummary>

        <AccordionDetails>{configurationComponent}</AccordionDetails>
      </Accordion>
    </div>
  );
}

function DeviceDetails(props: DeviceDetailsProps) {
  const fallbackComponent = (
    <div className="device-details">
      <Alert
        severity="error"
        sx={{ mb: 2, mt: 2, mx: 0.5, boxShadow: 2, minWidth: 700 }}
      >
        <AlertTitle>The device configuration has issues</AlertTitle>
        <Typography paragraph>The system may NOT resolve this automatically, you MUST contact technical support for assistance. </Typography>
      </Alert>
    </div>
  );

  return (
    <ErrorBoundary fallbackComponent={fallbackComponent}>
      <DeviceDetailsContent {...props} />
    </ErrorBoundary>
  );
}

export default connect((state: any) => {
  const { global } = state || {};

  const { shadow, attributes, connected } = state.system || {};

  return {
    shadow,
    attributes,
    connected,
    advanced: global.advanced,
    thing: state.system,
  };
}, null)(DeviceDetails as any);
